import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import '../styles/CosmicLoveBackground.css';

// ── PROCEDURAL TEXTURES (GPU Optimized & High Fidelity) ──

// Circular soft star texture
function createStarDotTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext('2d');
  const grad = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, 'rgba(255, 255, 255, 1)');
  grad.addColorStop(0.25, 'rgba(248, 250, 252, 0.9)');
  grad.addColorStop(0.55, 'rgba(253, 164, 175, 0.35)');
  grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 64, 64);
  return new THREE.CanvasTexture(canvas);
}

// Luminous shooting star flare head (Brilliant white core with rose glow)
function createMeteorHeadTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  const cx = 64;
  const cy = 64;

  // Intense central glow
  const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, 48);
  grad.addColorStop(0, 'rgba(255, 255, 255, 1)');
  grad.addColorStop(0.2, 'rgba(255, 241, 242, 0.95)');
  grad.addColorStop(0.5, 'rgba(253, 164, 175, 0.6)');
  grad.addColorStop(0.8, 'rgba(225, 29, 72, 0.2)');
  grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 128, 128);

  // Soft cinematic diffraction cross
  ctx.lineWidth = 1.5;
  const vGrad = ctx.createLinearGradient(cx, 0, cx, 128);
  vGrad.addColorStop(0, 'rgba(255, 255, 255, 0)');
  vGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.9)');
  vGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.strokeStyle = vGrad;
  ctx.beginPath();
  ctx.moveTo(cx, 16);
  ctx.lineTo(cx, 112);
  ctx.stroke();

  const hGrad = ctx.createLinearGradient(0, cy, 128, cy);
  hGrad.addColorStop(0, 'rgba(255, 255, 255, 0)');
  hGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.9)');
  hGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
  ctx.strokeStyle = hGrad;
  ctx.beginPath();
  ctx.moveTo(16, cy);
  ctx.lineTo(112, cy);
  ctx.stroke();

  return new THREE.CanvasTexture(canvas);
}

// Gentle ambient velvet nebula puff (Soft, non-distracting)
function createSoftNebulaTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 256;
  canvas.height = 256;
  const ctx = canvas.getContext('2d');
  const grad = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  grad.addColorStop(0, 'rgba(159, 10, 24, 0.20)');
  grad.addColorStop(0.45, 'rgba(110, 5, 28, 0.10)');
  grad.addColorStop(0.8, 'rgba(35, 4, 12, 0.03)');
  grad.addColorStop(1, 'rgba(0, 0, 0, 0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 256, 256);
  return new THREE.CanvasTexture(canvas);
}

const CosmicLoveBackground = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // ── 1. Scene, Camera & WebGL Renderer ──
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x070104, 0.0012);

    const camera = new THREE.PerspectiveCamera(
      45,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.set(0, 0, 105);

    const renderer = new THREE.WebGLRenderer({
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);

    const isMobile = window.innerWidth < 768;
    const starDotTex = createStarDotTexture();
    const meteorHeadTex = createMeteorHeadTexture();
    const softNebulaTex = createSoftNebulaTexture();

    // ── 2. ✨ MINIMAL & SERENE AMBIENT STARFIELD (Clean & Romantic) ──
    const STAR_COUNT = isMobile ? 380 : 720;
    const starGeo = new THREE.BufferGeometry();
    const starPos = new Float32Array(STAR_COUNT * 3);
    const starColors = new Float32Array(STAR_COUNT * 3);

    const palette = [
      new THREE.Color(0xf8fafc), // Crisp Silver
      new THREE.Color(0xe2e8f0), // Soft Platinum
      new THREE.Color(0xfda4af), // Gentle Rose
      new THREE.Color(0xfecdd3), // Soft Rosewater
      new THREE.Color(0xfb7185), // Velvet Rose
    ];

    const boundsX = isMobile ? 85 : 130;
    const boundsY = isMobile ? 65 : 95;

    for (let i = 0; i < STAR_COUNT; i++) {
      const i3 = i * 3;
      starPos[i3] = (Math.random() - 0.5) * boundsX * 2;
      starPos[i3 + 1] = (Math.random() - 0.5) * boundsY * 2;
      starPos[i3 + 2] = (Math.random() - 0.5) * 80 - 20;

      const col = palette[Math.random() < 0.65 ? 0 : Math.floor(Math.random() * palette.length)];
      starColors[i3] = col.r;
      starColors[i3 + 1] = col.g;
      starColors[i3 + 2] = col.b;
    }

    starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
    starGeo.setAttribute('color', new THREE.BufferAttribute(starColors, 3));

    const starMat = new THREE.PointsMaterial({
      size: isMobile ? 1.6 : 1.9,
      map: starDotTex,
      vertexColors: true,
      transparent: true,
      opacity: 0.82,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const starMesh = new THREE.Points(starGeo, starMat);
    scene.add(starMesh);

    // Occasional gentle bright shimmering stars (~24 stars)
    const SHIMMER_COUNT = isMobile ? 16 : 28;
    const shimmerGeo = new THREE.BufferGeometry();
    const shimmerPos = new Float32Array(SHIMMER_COUNT * 3);
    for (let i = 0; i < SHIMMER_COUNT; i++) {
      shimmerPos[i * 3] = (Math.random() - 0.5) * boundsX * 1.6;
      shimmerPos[i * 3 + 1] = (Math.random() - 0.5) * boundsY * 1.6;
      shimmerPos[i * 3 + 2] = (Math.random() - 0.5) * 40;
    }
    shimmerGeo.setAttribute('position', new THREE.BufferAttribute(shimmerPos, 3));
    const shimmerMat = new THREE.PointsMaterial({
      size: isMobile ? 3.2 : 4.2,
      map: starDotTex,
      color: 0xffffff,
      transparent: true,
      opacity: 0.9,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const shimmerMesh = new THREE.Points(shimmerGeo, shimmerMat);
    scene.add(shimmerMesh);

    // ── 3. ☁️ SUBTLE AMBIENT VELVET NEBULA PUFFS (Soft Atmospheric Depth) ──
    const NEBULA_COUNT = isMobile ? 4 : 7;
    const nebulaGeo = new THREE.BufferGeometry();
    const nebulaPos = new Float32Array(NEBULA_COUNT * 3);
    for (let i = 0; i < NEBULA_COUNT; i++) {
      let x = (Math.random() - 0.5) * boundsX * 1.4;
      let y = (Math.random() - 0.5) * boundsY * 1.4;
      // Keep center region clear so counter text and cards remain crystal clear
      if (Math.abs(x) < 32 && Math.abs(y) < 25) {
        x += x >= 0 ? 32 : -32;
      }
      nebulaPos[i * 3] = x;
      nebulaPos[i * 3 + 1] = y;
      nebulaPos[i * 3 + 2] = -35 + (Math.random() - 0.5) * 15;
    }
    nebulaGeo.setAttribute('position', new THREE.BufferAttribute(nebulaPos, 3));
    const nebulaMat = new THREE.PointsMaterial({
      size: isMobile ? 65 : 95,
      map: softNebulaTex,
      transparent: true,
      opacity: 0.32,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const nebulaMesh = new THREE.Points(nebulaGeo, nebulaMat);
    scene.add(nebulaMesh);

    // ── 4. 🌠 CINEMATIC SHOOTING STARS (Distinct, Graceful & Prominent) ──
    const METEOR_COUNT = 3;
    const meteors = [];

    for (let m = 0; m < METEOR_COUNT; m++) {
      const angle = (Math.PI / 180) * (32 + Math.random() * 8);
      const tailLen = isMobile ? 34 : 48;

      // Tail streak
      const trailGeo = new THREE.BufferGeometry();
      const points = new Float32Array([
        0, 0, 0,
        Math.cos(angle) * tailLen, Math.sin(angle) * tailLen, 0
      ]);
      const colors = new Float32Array([
        1, 1, 1,           // Bright luminous core at head
        0.99, 0.65, 0.74   // Rose shimmer fade along tail
      ]);
      trailGeo.setAttribute('position', new THREE.BufferAttribute(points, 3));
      trailGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

      const trailMat = new THREE.LineBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
      });
      const trailLine = new THREE.Line(trailGeo, trailMat);

      // Glowing star head point
      const headGeo = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0)]);
      const headMat = new THREE.PointsMaterial({
        size: isMobile ? 13 : 18,
        map: meteorHeadTex,
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      });
      const headPoint = new THREE.Points(headGeo, headMat);

      const meteorGroup = new THREE.Group();
      meteorGroup.add(trailLine);
      meteorGroup.add(headPoint);
      scene.add(meteorGroup);

      meteors.push({
        group: meteorGroup,
        trailLine,
        headPoint,
        active: false,
        progress: 0,
        speed: 0,
        cooldown: 0.6 + m * 2.2, // Staggered initial starts
        angle,
        vx: 0,
        vy: 0,
      });
    }

    const resetMeteor = (m) => {
      // Spawn in upper visible region to cross the viewport smoothly
      const spawnX = Math.random() * (isMobile ? 60 : 95) - (isMobile ? 10 : 20);
      const spawnY = Math.random() * (isMobile ? 25 : 35) + (isMobile ? 15 : 22);
      const spawnZ = Math.random() * 15 - 5;
      m.group.position.set(spawnX, spawnY, spawnZ);

      m.angle = (Math.PI / 180) * (32 + Math.random() * 8);
      const speed = isMobile ? 50 : 68;
      m.vx = -Math.cos(m.angle) * speed;
      m.vy = -Math.sin(m.angle) * speed;
      m.speed = speed;
      m.progress = 0;
      m.active = true;
    };

    // ── 5. 💫 INTERACTIVE WISH STARDUST SPARKS ──
    const MAX_WISH_SPARKS = 40;
    const wishGeo = new THREE.BufferGeometry();
    const wishPositions = new Float32Array(MAX_WISH_SPARKS * 3);
    const wishColors = new Float32Array(MAX_WISH_SPARKS * 3);
    const wishSparks = [];

    for (let i = 0; i < MAX_WISH_SPARKS; i++) {
      wishPositions[i * 3] = 9999;
      wishPositions[i * 3 + 1] = 9999;
      wishPositions[i * 3 + 2] = 9999;
      wishColors[i * 3] = 1;
      wishColors[i * 3 + 1] = 1;
      wishColors[i * 3 + 2] = 1;

      wishSparks.push({
        active: false,
        x: 0,
        y: 0,
        z: 0,
        vx: 0,
        vy: 0,
        vz: 0,
        life: 0,
        maxLife: 1,
      });
    }

    wishGeo.setAttribute('position', new THREE.BufferAttribute(wishPositions, 3));
    wishGeo.setAttribute('color', new THREE.BufferAttribute(wishColors, 3));

    const wishMat = new THREE.PointsMaterial({
      size: 2.6,
      map: starDotTex,
      vertexColors: true,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });
    const wishMesh = new THREE.Points(wishGeo, wishMat);
    scene.add(wishMesh);

    const triggerWishSparks = (screenX, screenY) => {
      const vec = new THREE.Vector3(
        (screenX / window.innerWidth) * 2 - 1,
        -(screenY / window.innerHeight) * 2 + 1,
        0.5
      );
      vec.unproject(camera);
      const dir = vec.sub(camera.position).normalize();
      const distance = -camera.position.z / dir.z;
      const targetPos = camera.position.clone().add(dir.multiplyScalar(distance));

      let spawned = 0;
      for (let i = 0; i < MAX_WISH_SPARKS && spawned < 16; i++) {
        const s = wishSparks[i];
        if (!s.active) {
          s.active = true;
          s.x = targetPos.x + (Math.random() - 0.5) * 3;
          s.y = targetPos.y + (Math.random() - 0.5) * 3;
          s.z = targetPos.z;
          const angle = Math.random() * Math.PI * 2;
          const speed = Math.random() * 10 + 3;
          s.vx = Math.cos(angle) * speed;
          s.vy = Math.sin(angle) * speed + 3;
          s.vz = (Math.random() - 0.5) * 4;
          s.life = 1;
          s.maxLife = Math.random() * 0.4 + 0.6;
          spawned++;
        }
      }
    };

    // ── 6. PARALLAX & ANIMATION LOOP ──
    let targetMouseX = 0;
    let targetMouseY = 0;
    let mouseX = 0;
    let mouseY = 0;

    const onPointerMove = (e) => {
      const halfW = window.innerWidth / 2;
      const halfH = window.innerHeight / 2;
      targetMouseX = ((e.clientX - halfW) / halfW) * 5;
      targetMouseY = ((e.clientY - halfH) / halfH) * 4;
    };

    const onPointerDown = (e) => {
      if (e.target.closest('button, input, [role="button"], .mini-games-modal, .login-box')) {
        return;
      }
      triggerWishSparks(e.clientX, e.clientY);
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerdown', onPointerDown, { passive: true });

    const onResize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    let animationId;
    let lastTime = performance.now();

    const animate = (time) => {
      animationId = requestAnimationFrame(animate);
      const delta = Math.min((time - lastTime) * 0.001, 0.1);
      lastTime = time;

      // Smooth camera tilt parallax
      mouseX += (targetMouseX - mouseX) * 0.04;
      mouseY += (targetMouseY - mouseY) * 0.04;
      camera.position.x = mouseX;
      camera.position.y = -mouseY;
      camera.lookAt(0, 0, 0);

      // Very subtle slow celestial drift (calm & relaxing)
      starMesh.rotation.y = Math.sin(time * 0.00015) * 0.04;
      starMesh.rotation.x = Math.cos(time * 0.00012) * 0.03;

      // Shimmer star pulse
      const shimmerPulse = 0.8 + Math.sin(time * 0.002) * 0.2;
      shimmerMat.opacity = shimmerPulse;

      // Meteors update (clearly visible & prominent)
      meteors.forEach((m) => {
        if (!m.active) {
          m.cooldown -= delta;
          if (m.cooldown <= 0) {
            resetMeteor(m);
            m.cooldown = Math.random() * 3 + 2.5; // Frequent: streaks every 2.5 - 5.5s
          }
        } else {
          m.progress += delta * 0.95;
          m.group.position.x += m.vx * delta;
          m.group.position.y += m.vy * delta;

          // Prominent sine curve for glowing fade-in and graceful exit
          const curve = Math.sin(Math.min(1, Math.max(0, m.progress)) * Math.PI);
          m.trailLine.material.opacity = curve * 0.95;
          m.headPoint.material.opacity = curve * 1.0;

          if (m.progress >= 1) {
            m.active = false;
            m.trailLine.material.opacity = 0;
            m.headPoint.material.opacity = 0;
          }
        }
      });

      // Wish sparks update
      let anyWishActive = false;
      const wPos = wishGeo.attributes.position.array;
      const wCol = wishGeo.attributes.color.array;

      for (let i = 0; i < MAX_WISH_SPARKS; i++) {
        const s = wishSparks[i];
        if (s.active) {
          anyWishActive = true;
          s.life -= delta / s.maxLife;

          if (s.life <= 0) {
            s.active = false;
            wPos[i * 3] = 9999;
            wPos[i * 3 + 1] = 9999;
            wPos[i * 3 + 2] = 9999;
          } else {
            s.x += s.vx * delta;
            s.y += s.vy * delta;
            s.z += s.vz * delta;
            s.vx *= 0.96;
            s.vy -= 1.4 * delta;

            wPos[i * 3] = s.x;
            wPos[i * 3 + 1] = s.y;
            wPos[i * 3 + 2] = s.z;

            const fade = Math.max(0, s.life);
            wCol[i * 3] = fade;
            wCol[i * 3 + 1] = fade * 0.85;
            wCol[i * 3 + 2] = fade * 0.95;
          }
        }
      }

      if (anyWishActive) {
        wishGeo.attributes.position.needsUpdate = true;
        wishGeo.attributes.color.needsUpdate = true;
      }

      renderer.render(scene, camera);
    };

    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('resize', onResize);

      if (renderer.domElement && container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
      starGeo.dispose();
      starMat.dispose();
      shimmerGeo.dispose();
      shimmerMat.dispose();
      nebulaGeo.dispose();
      nebulaMat.dispose();
      starDotTex.dispose();
      meteorHeadTex.dispose();
      softNebulaTex.dispose();
      meteors.forEach((m) => {
        m.trailLine.geometry.dispose();
        m.trailLine.material.dispose();
        m.headPoint.geometry.dispose();
        m.headPoint.material.dispose();
      });
      wishGeo.dispose();
      wishMat.dispose();
    };
  }, []);

  return (
    <div className="cosmic-love-background" aria-hidden="true">
      <div ref={containerRef} className="webgl-canvas-container" />
      <div className="cosmic-nebula-vignette" />
    </div>
  );
};

export default CosmicLoveBackground;
